import type { RefObject } from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import type { EmojiProvider } from '@atlaskit/emoji/resource';
import type { HeadingProps } from '@atlaskit/heading/heading';
import type { MentionProvider } from '@atlaskit/mention/types';
import { type AgentsConfig } from '@atlassian/conversation-assistant-agent/controllers/agent-overrider';
import type { BrowserContextSanitizer } from '@atlassian/conversation-assistant-chat-prompt-input/types';
import type { EditorPresetProps } from '@atlassian/conversation-assistant-content-renderer/controllers/editor';
import type { CreationContextParams } from '@atlassian/conversation-assistant-external-context/types';
import type { FollowUpSource } from '@atlassian/conversation-assistant-instrumentation/constants/analytics';
import { type ActionContext } from '@atlassian/conversation-assistant-message-actions-api/types';
import { type AssistanceServiceProduct } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type { UrlValidationConfig } from '@atlassian/conversation-assistant-ui-components/types';
import {
	type ConversationAction,
	type ConversationActionTags,
} from '@atlassian/rovo-conversation-actions/types';

import { type DeepPartial } from './common/types';

export type ProfileData = {
	aaid?: string;
	name: string;
	displayName?: string;
	avatarUrl?: string;
	email?: string;
};

export type PromptSentContext = {
	/** Analytics classification of the surface where the prompt was sent. */
	pageContext: string;
	contentId?: string | number;
	agentIdentityAccountId?: string;
};

/**
 * A page body offered by the host, carried with the id of the page it was read
 * from.
 *
 * The id travels with the content because the consumer cannot otherwise tell a
 * stale body from a current one: an SPA transition changes the page long before
 * whatever cache the host reads from catches up.
 *
 * `content` may arrive parsed or serialised — it crosses a package boundary that
 * some hosts serialise over — and `representation` says how to read it. Only ADF
 * is accepted today; the field exists so another format can be added without
 * guessing at the one already in use.
 */
export type AutocompletePageContent = {
	contentId: string;
	content: DocNode | string;
	representation: 'atlas_doc_format';
};

export type AutocompletePageContentSource = {
	/**
	 * Resolves the body of the page currently open, or `undefined` when there is
	 * nothing to offer. Called on demand rather than eagerly, so a page nobody
	 * types against is never read.
	 */
	getCurrentContent: () => Promise<AutocompletePageContent | undefined>;
	/**
	 * Notifies when the answer to `getCurrentContent` may have changed. Returns an
	 * unsubscribe.
	 */
	subscribe: (listener: () => void) => () => void;
};

/**
 * Resolves the id of the editor/content currently open, for AIFC write actions that need one but
 * received none. Returning `''` or `undefined` falls through to the existing resolution chain.
 */
export type EditorContentIdResolver = () => string | undefined;

export type ContextualPromptAnalyticsMetadata = {
	experimentKey: 'JSMGROWTH-524';
	experimentCohort?: string;
	routeName?: string;
	/** Route *pattern* rather than the resolved URL, so no project keys are emitted. */
	routePath?: string;
	/** Raw project template key; absent when the host could not resolve it. */
	projectTemplateId?: string;
	/** Template bucket that selected the prompt set: `IT`, `HR`, `General`, or `unknown`. */
	templateGroup?: string;
	pageContext?: string;
	promptSetId?: string;
	promptMetadata?: { promptId: string; promptCategory: string }[];
	daysSinceSignupBucket?: string;
	daysSinceJsmActivationBucket?: string;
	viewedAt?: number;
};

export type ConversationAssistantConfig = {
	/**
	 * Host-owned source for the page body backing contextual autocomplete.
	 * Implementations must bind content to its content id so SPA transitions
	 * cannot expose a previous page as the current one.
	 */
	autocompletePageContentSource?: AutocompletePageContentSource;
	/**
	 * Called when a prompt send is initiated and contains the page context.
	 *
	 * This callback is not called when chat opens or for contextless prompts. It
	 * must be non-blocking: failures must not affect delivery of the chat prompt.
	 */
	onPromptSent?: (context: PromptSentContext) => void | Promise<void>;
	/**
	 * Optional contextual prompt metadata for host-owned experiments.
	 * When present, closeChatButton analytics can include prompt context.
	 */
	contextualPromptAnalyticsMetadata?: ContextualPromptAnalyticsMetadata;
	/** Optional analytics-only product header override. Does not affect Assistance Service requests. */
	analyticsProductHeader?: string;
	assistanceServiceConfig?: {
		baseUrl?: string;
		headers: Record<string, string>;
		minionAlias?: string;
		channelId?: string;
	};
	/**
	 * Skills required for every turn of this session, sent as `skills` on the assistance request.
	 * Stronger than `assistanceServiceConfig.minionAlias`, which only suggests a minion and
	 * leaves tool selection to the backend.
	 */
	requestedSkills?: string[];
	/**
	 * Creation context applied after the staging-area modal's own Create-with-Rovo defaults, so a
	 * non-Confluence host can replace `source` and `experience`. Those defaults route backend
	 * agent selection towards Confluence content creation, which is wrong for a host that creates
	 * something else. Setting `source` also stops follow-up turns being relabelled as `cwr`.
	 */
	creationContextOverrides?: CreationContextParams;
	/**
	 * Optional override for the user-facing host (origin) used to build
	 * shareable chat links.
	 *
	 * When omitted, links default to the appropriate Home host so that recipients
	 * are always routed to the shared chat viewer regardless of which tenant/origin
	 * the sender created the chat from. Staging tenants on jira-dev.com and
	 * staging Home default to staging Home; other origins default to production
	 * Home. Set this primarily so callers can generate links against a specific
	 * non-production Home host (e.g. `https://home.stg.atlassian.com`).
	 */
	shareBaseUrl?: string;
	displaySubmitFlag?: boolean;
	orgId?: string;
	siteId: string;
	/**
	 * Jira workspace activation id. Required for ERS-sharded queries such as
	 * `agent_sessionAssociationQuery`, which look up
	 * `ari:cloud:jira:{siteId}:workspace/{activationId}`.
	 */
	activationId?: string;
	userId?: string;
	product: AssistanceServiceProduct;
	emojiProvider?: Promise<EmojiProvider>;
	/**
	 * Jira-owned providers used by the built-in issue actions in the session right panel.
	 * The issue key is resolved from the session association query, not the current page.
	 */
	jiraIssueCommentEditor?: {
		createMentionProvider: (issueKey: string) => Promise<MentionProvider>;
		onCommentSave: (issueKey: string, comment: DocNode) => Promise<void>;
	};
	/**
	 * If true, errors will be re-thrown after being handled by the error boundary
	 * @default false
	 */
	rethrowErrors?: boolean;
	/**
	 * If true, abort requests will not be called when the preview modal unmounts
	 * This is used to prevent streaming from cancelling while other conversation assistants are still visible
	 * @default false
	 */
	preventAbortRequestsOnStagingModalClose?: boolean;
	/**
	 * If true, abort requests will not be called when the standalone (i.e. non-modal) chat is closed
	 * This is used to prevent streaming from cancelling in case the user quickly reloads the chat window (i.e. by closing and opening the rovo sidebar)
	 * @default false
	 */
	preventAbortRequestsOnStandaloneChatClose?: boolean;
	/** Only pass for BITBUCKET and TRELLO */
	workspaceId?: string;
	experienceId?: string;
	renderingTouchpoint?: ActionContext['renderingTouchpoint'];
	/**
	 * Optional context about the current page/content the user is viewing.
	 * Used by the smart creation flow to enable "Add to page" functionality
	 * when the ephemeral preview is opened from the Rovo Chat preview card.
	 * Should be set by the host application (e.g. Confluence) when the chat is
	 * mounted on a content page.
	 */
	smartCreationContext?: {
		confluence?: {
			content?: {
				id: string;
				type: string;
				subType?: 'live' | null;
			};
		};
		/**
		 * Generic context for non-Confluence products (e.g. Jira).
		 * Provide both fields so the finalize area can route the insert command
		 * to the correct editor instance AND send the right container ARI to the
		 * duplicate remix API.
		 */
		editor?: {
			/**
			 * Pre-formed container ARI for the duplicate remix API call
			 * (e.g. `ari:cloud:jira:<cloudId>:issue/<issueId>`).
			 */
			containerAri: string;
			/**
			 * Editor instance UUID (as registered with the rovo content bridge via
			 * `getConfluenceEditorClientId`). Used to route `EditorInsertContentCommand`
			 * to the correct editor when `confluence.content.id` is not set.
			 */
			editorId: string;
		};
	};
	/**
	 * Optional attribute to identify the product surface location where Chat & Search is engaged from.
	 * Used for analytics to distinguish traffic from different Home surfaces.
	 */
	inProductExperience?: string;
	/**
	 * The host product identifier used exclusively for cross-product (XPC) URL wrapping analytics
	 * (e.g. 'townsquare', 'confluence', 'jira'). Does not affect link resolution or assistance service routing.
	 * Takes precedence over `product` when determining the source product for XPC URL wrapping.
	 */
	xpcProduct?: string;
	/**
	 * The bridge identifier used exclusively for cross-product (XPC) URL wrapping analytics
	 * (e.g. 'rovo.extension'). Does not affect link resolution or assistance service routing.
	 * Takes precedence over the default Rovo Chat bridge derived from the UI mode.
	 */
	xpcBridge?: string;
	/**
	 * The host sub-product identifier used exclusively for cross-product (XPC) URL wrapping analytics
	 * (e.g. 'townsquare-goals', 'townsquare-kudos'). Does not affect link resolution.
	 */
	xpcSubProduct?: string;
	/** See `EditorContentIdResolver`. */
	editorContentIdResolver?: EditorContentIdResolver;
	/**
	 * Opt-in loader for on-device chat autocomplete (`@mlc-ai/web-llm`).
	 * Import `loadLocalModelAutocomplete` from
	 * `@atlassian/conversation-assistant/local-model` and pass it here.
	 * Hosts that omit this (including Trello) never download web-llm.
	 */
	loadLocalModelAutocomplete?: EditorPresetProps['loadAutocompletePlugin'];
};

export type UIConfig = DeepPartial<{
	externalEmbed: boolean;
	fullScreen: boolean;
	/**
	 * Keeps the conversation ID URL parameter after hydrating an existing conversation.
	 * @default false
	 */
	preserveUrlConversationId: boolean;
	/**
	 * Optional text to prefill in the chat prompt input without automatically sending.
	 */
	initialPromptText?: string;
	/**
	 * When true, treats `initialPromptText` as a one-shot seeded prompt instead of a persistent
	 * prefill so the user can clear it with Backspace before editing.
	 */
	shouldSeedInitialPromptText?: boolean;
	/**
	 * When set, the embedded chat creates a fresh backend-backed conversation on mount instead of
	 * opening on the shared `EMPTY_CONVERSATION_ID`, so the first send performs no `EMPTY -> real`
	 * transition (which rebuilds the chat view and re-applies a seeded prompt). Intended for
	 * embedded, one-off hosts such as the Teamwork Graph modal.
	 */
	createConversationOnMount?: {
		/**
		 * Analytics launch-source label recorded on the created conversation (emitted as the GASv3
		 * `launchSource` attribute). Identifies the host that opened the chat.
		 */
		followUpSource: FollowUpSource;
	};
	/**
	 * When true and `fullScreen` is true, renders the conversation assistant as a
	 * left/right split layout: the chat lives on the left in a resizable column,
	 * and a right-hand panel renders the chat result "Sources" + "Output" artefacts
	 * @default false
	 */
	isContextPanelEnabled: boolean;
	isBitbucketBetaEnabled: boolean;
	customFeedbackCategories?: {
		positive: readonly string[];
		negative: readonly string[];
	};
	/**
	 * If true, enables the context banner to follow browser context from external sites.
	 * When enabled, the context banner will display browser context when available.
	 * @default false
	 */
	enableBrowserContextBanner?: boolean;
	/**
	 * If true, enables content preview support in the smart creation modal.
	 * This should be set to true for all products using smart create modal or
	 * staging area ref.
	 * @default true
	 */
	isContentPreviewSupported?: boolean;
	/**
	 * When true, fullscreen chat does not update `document.title` with the agent/conversation title.
	 * @default false
	 */
	hideBrowserTabTitle?: boolean;
	/**
	 * When true, fullscreen chat does not replace the browser tab favicon with the selected agent icon.
	 * @default false
	 */
	hideBrowserTabFavicon?: boolean;
	/**
	 * When true, the Rovo settings modal is disabled and all triggers
	 * (app filter, header settings, memory spotlight) become no-ops.
	 * Use this for constrained viewports such as the Chrome extension sidebar
	 * where the large modal cannot render properly.
	 * @default false
	 */
	disableSettingsModal?: boolean;
	/**
	 * When false, skills (slash-command typeahead entries) are hidden from the
	 * chat prompt input.
	 * @default true
	 */
	enableSkills?: boolean;
	/**
	 * Controls whether agents can be @-mentioned in the chat prompt input.
	 * @default false
	 */
	enableAgentMentions?: boolean;
	/**
	 * When false, the auto-suggest overlay (suggestions that appear
	 * in the chat body based on the user's input) is hidden.
	 * External consumers such as Trello that do not want the suggested
	 * panel can set this to false.
	 * @default true
	 */
	enableAutoSuggest?: boolean;
	/**
	 * When false, disables resize behavior for the mini modal.
	 * @default true
	 */
	enableMiniModalResize?: boolean;
	elements: {
		closeButton: boolean;
		backButton: boolean;
		newChatButton: boolean;
		fullscreenButton: boolean;
		createAgentButton: boolean;
		browseAgentsButton: boolean;
		agentsMenu: boolean;
		navbar: boolean;
		mainChatHeader: boolean;
		stagingAreaEnabled: boolean;
		voiceDictationButton: boolean;
		/**
		 * Controls whether source filters and reasoning-related footer controls are shown.
		 * @default true
		 */
		chatModeMenuButton: boolean;
		/**
		 * Controls whether the Max mode (task) option is shown in the reasoning mode selector.
		 * Set to false for CWR (Create With Rovo) and other non-chat entry points.
		 * @default true
		 */
		maxModeButton: boolean;
		fileUploadButton: boolean;
		forceFullscreenHeader: boolean;
		/**
		 * Controls whether the greeting is displayed in the empty state.
		 * When true, the greeting is shown
		 * When false, the greeting is always hidden regardless of mode.
		 * When undefined, the greeting is shown in all modes except mini modal, where it is hidden. This is to accommodate the limited space in mini modal.
		 * @default undefined
		 */
		shouldShowGreeting?: boolean;
		/**
		 * Controls whether suggested conversation actions are displayed in the empty state.
		 * When false, the conversation actions segment is not rendered.
		 * @default true
		 */
		shouldShowConversationActions?: boolean;
		/**
		 * Controls whether the current context banner can be displayed.
		 * When false, automatic current-page context is not injected into the backend payload.
		 * Consumers can still provide explicit, separately managed backend context.
		 * @default true
		 */
		shouldShowContextBanner?: boolean;
	};
	/**
	 * Overrides the site picker section heading in the chat header more-menu.
	 * When omitted, the heading defaults to "Switch site".
	 */
	sitePickerMenuLabel?: string;
}> & {
	/**
	 * Optional custom greeting configuration. When provided, a CustomGreetingCard will be
	 * rendered with the given icon and title instead of the default RovoGreeting.
	 */
	customGreeting?: {
		/** The icon source URL for light theme. Falls back to default icon if not provided */
		iconSrc?: string;
		/** The icon source URL for dark theme. Falls back to `iconSrc` if not provided. */
		iconSrcDark?: string;
		/** The greeting title text. Limited to 50 characters. Falls back to default greeting text if not provided */
		title?: string;
		/**
		 * Optional secondary text rendered directly below the title at a smaller,
		 * subdued style. Useful for a short call-to-action or onboarding context
		 * line.
		 */
		subtitle?: string;
		/** Optional test ID. Will be prefixed with 'custom-rovo-greeting-'. */
		testId?: string;
		/** Duration of the fade-in animation. @default 'large' */
		animationDuration?: 'small' | 'medium' | 'large';
		/** Direction the greeting enters from. @default 'bottom' */
		entranceDirection?: 'top' | 'bottom' | 'left' | 'right';
		/** Direction the greeting exits to. @default 'top' */
		exitDirection?: 'top' | 'bottom' | 'left' | 'right';
		/** Key for the FadeIn animation to control mount/unmount transitions. @default 'custom-greeting' */
		animationKey?: string;
		/** Allow consumer to override the greeting truncation logic */
		truncateGreeting?: boolean;
		/** Size of the title heading. Accepts any value supported by the Heading component's size prop. @default 'xlarge' */
		titleSize?: HeadingProps['size'];
		/** Optional image height in pixels. @default 80 */
		imageHeight?: number;
	};
	customHandlers?: {
		handleSubmitSmartCreate?: ({ baseUrl, path }: { baseUrl: string; path: string }) => void;
		handleCloseSmartCreate?: ({
			currentConversationId,
		}: {
			currentConversationId?: string | null;
		}) => void;
		/**
		 * Callback that allows the host application to handle a specialized deep link for SPA routing.
		 * deep links are identified by the presence of a `deepLinkTarget` query parameter in the URL of clicked links.
		 * Ex of url sent to callback: https://example.atlassian.net/some-path?deepLinkTarget=jira#section
		 * Use in conjunction with the config.product prop and its internally mapped ProductKey in packages/ai-mate/conversation-assistant/src/common/utils/global-config/index.ts
		 * to enable deep links to be handled by the host application.
		 */
		handleDeepLinkClick?: (url: string) => void;
		/**
		 * Optional onClick handler when the user chooses "Browse agents" in the header agent dropdown.
		 * If not supplied, the default behavior is to open the browse agents modal within the conversation assistant.
		 */
		onBrowseAgentClick?: () => void;
		/**
		 * Optional onClick handler when the user chooses "Create agent" in the header agent dropdown.
		 * If not supplied, the default behavior is to open the agent creation flow in Atlassian Studio.
		 */
		onCreateAgentClick?: () => void;
		/**
		 * Optional onClick handler for the "Share" entry in the chat header's more menu.
		 * Share entry is rendered in the more menu and this handler is invoked.
		 */
		onShareClick?: () => void;
	};
	/**
	 * Optional sanitizer for browser context URLs. When provided, URLs will be checked
	 * against the sanitizer's blocklist before being included in browser context.
	 */
	browserContextSanitizer?: BrowserContextSanitizer;
	promptInputDroppableUrls?: UrlValidationConfig;
	/**
	 * Ref to the external container element that the smart-creation staging area is
	 * portalled into. Kept outside the `DeepPartial` block so the `RefObject` type is
	 * preserved atomically (a deep-partial ref would lose `current`'s element type).
	 */
	stagingAreaRef?: RefObject<HTMLDivElement>;
	/**
	 * Optional host-owned content rendered immediately above the default chat input.
	 * Use this for persistent product functionality that must not compete with
	 * Post Office placements.
	 */
	aboveChatInput?: React.ReactNode;
	/**
	 * Changing this value requests focus for the default chat input without
	 * focusing it on the initial render.
	 */
	chatInputFocusRequestKey?: number;
};

export interface AgentVersioningConfig {
	/**
	 * Shows version picker in agent empty state.
	 * Only displayed for users with admin/collaborator permission to the agent.
	 * @default false
	 */
	showVersionPicker?: boolean;

	/**
	 * Preserves selected version when creating new chat from current conversation.
	 * When false/undefined, new chats default to LIVE version.
	 * @default false
	 */
	preserveVersionOnNewChat?: boolean;
}

export type ConversationAssistantModeConfig = {
	/**
	 * When provided, overrides the default UI mode.
	 * This allows consumers to use render the conversation assistant in mini modal mode.
	 * @default ConversationAssistantUIModes.SIDEBAR
	 */
	mode: ConversationAssistantUIModes;

	/**
	 * Whether mini-modal mode is currently visible. Consumers may keep the modal
	 * mounted during its closing animation, so `mode` alone cannot represent an
	 * open transition.
	 * @default true
	 */
	isOpen?: boolean;

	/**
	 * Indicates whether mini modal mode is available to be switched to in the consumer's context.
	 * Leverage the @atlassian/rovo-experience-api for this information
	 * This allows consumers to conditionally enable or disable mini modal mode.
	 */
	isMiniModalAvailable: boolean;

	/**
	 * Indicates whether sidebar mode is available to be switched to in the consumer's context.
	 * Leverage the @atlassian/rovo-experience-api for this information
	 * This allows consumers to conditionally enable or disable sidebar mode.
	 * If not provided, it defaults to true.
	 */
	isSidebarAvailable?: boolean;

	/**
	 * A callback, that enables the mode switching intent triggering from within conversation assistant,
	 * the actual switching happens outside this component, if the intention is to change mode of the same instance component,
	 * change in mode prop is warranted from consumer in response to this callback.
	 */
	switchToMode: (mode: ConversationAssistantUIModes | undefined) => void;
};

export interface ConversationAssistantProps {
	/**
	 * A `testId` prop is provided for specified elements, which is a unique
	 * string that appears as a data attribute `data-testid` in the rendered code,
	 * serving as a hook for automated tests */
	testId?: string;

	/**
	 * Callback used to display a "close" button and trigger the action provided by the consumer.
	 */
	onCloseChat?: () => void;

	/**
	 * Provides credentials and other settings
	 */
	config: ConversationAssistantConfig;

	/**
	 * Provides configurable UI settings so integrators can customize the appearance and behavior suitable for their context.
	 */
	uiConfig?: Partial<UIConfig>;

	/**
	 * Provides an option to configure the agents.
	 */
	agentsConfig?: AgentsConfig;

	/**
	 * Configuration for agent versioning features.
	 * When provided, enables versioning-related UI and behaviors.
	 * Typically used in Studio context.
	 */
	agentVersioningConfig?: AgentVersioningConfig;

	/**
	 * Provides a function to render a custom site picker.
	 */
	sitePickerRender?: JSX.Element;

	/**
	 * When provided, replaces the default conversation assistant UI with custom React content.
	 * This allows consumers to initialize the chat state when the side panel chat is not rendered yet.
	 */
	overridingChildren?: React.ReactNode;

	/**
	 * Skips the chat UI: the shared providers, the global chat store and the bridge API handlers are
	 * still mounted, and `overridingChildren` (if any) still renders. Use this for mounts that exist
	 * purely to initialise that layer (e.g. an app root), so the chat UI module graph is never
	 * rendered and never downloaded.
	 *
	 * Defaults to `Boolean(overridingChildren)`: passing children has always implied "no default chat
	 * UI", so existing consumers keep their current behaviour without opting in.
	 */
	skipChatUI?: boolean;

	/**
	 * When true, uses the overriding children as the fallback component when the conversation assistant is loading.
	 * @default false
	 */
	useChildrenAsLoadingFallback?: boolean;

	/**
	 * When provided, overrides the default mode and switchToMode configuration.
	 * This allows consumers to render the conversation assistant in mini modal mode and switch between modes.
	 * By default, the conversation assistant will be rendered in sidebar mode without ability to switch modes
	 * if this prop is not provided.
	 */
	modeConfig?: ConversationAssistantModeConfig;

	/**
	 * An async callback that returns the actions to be displayed in the conversation assistant.
	 * This is used to override the actions that are displayed in the conversation assistant.
	 */
	getActions?: () => Promise<ConversationAction[]>;

	/**
	 * When true, only the actions fetched from the custom getActions provider are displayed,
	 * rather than being merged with the default actions (dynamic starters, prompt-of-day, etc).
	 *
	 * Note: if setEmptyChatLaunch is called with replaceProviderActions=true at runtime,
	 * the store's actions will take precedence over getActions entirely — this prop then
	 * controls display behaviour (suppressing dynamic starters) for those store actions.
	 * @default false
	 */
	prioritizeCustomActionResults?: boolean;

	/**
	 * The number of conversation starters to show.
	 * @default 3
	 */
	numStartersToShow?: number;

	/**
	 * The vertical offset (in pixels) from the top of the page layout. This value is used to adjust the mini modal's
	 * max height, to prevent it from overlapping with any components at the top of the page (like the navbar).
	 */
	topOffset?: number;
	/**
	 * Tags configuration for skill metadata fetching.
	 * When provided, these tags are used to fetch relevant skill metadata from the backend
	 * via RovoSkillsConfigProvider context.
	 */
	tags?: ConversationActionTags;

	/**
	 * When provided, the conversation assistant will automatically open a new chat
	 * and send this prompt as the first message. Used by the embedded Confluence editor
	 * to trigger Rovo chat with a pre-filled prompt from a Jira nudge.
	 */
	initialPrompt?: string;

	/**
	 * When true, suppresses the initial chat input focus.
	 */
	suppressInitialFocus?: boolean;

	/**
	 * When provided, the chat store forcibly restores this exact conversation on
	 * hydration, bypassing the normal restorability/expiry heuristics. Used by
	 * Rovodal hosts that open chat targeting a specific conversation (e.g. from a
	 * "view conversation" entry point) where the ambient restore logic could
	 * otherwise select a different (or empty) conversation.
	 */
	forceRestoreConversationId?: string;
}

/* ConversationAssistantUIModes is duplicated in rovo-experience-api-store package
   to avoid circular dependencies. Any changes here should be mirrored in
	 platform/packages/ai-mate/rovo-experience-api/src/services/rovo-experience-api-store/types.ts
*/
export const ConversationAssistantUIModes = {
	SIDEBAR: 'sidebar',
	MINI_MODAL: 'mini-modal',
} as const;

export type ConversationAssistantUIModes =
	(typeof ConversationAssistantUIModes)[keyof typeof ConversationAssistantUIModes];

export interface ConversationAssistantInternalProps {
	testId?: string;
	sitePickerRender?: JSX.Element;
	stagingAreaRef?: RefObject<HTMLDivElement>;
}
